import { useContext, useState } from 'react'
import { useFormikContext } from 'formik'
import { FormContent, ButtonDrawer } from '~/components'
import { StepperContext } from '~/components/ui/ViewStepper'
import { addEmail, completeActivation, isBadState } from '~/lib/api'
import { emitCustomEvent } from 'react-custom-events'
import { StoreContext } from '~/store'
import { useTranslation } from 'react-i18next'

const UserIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
)

export default function ConfirmExistingProfile() {
  const { t } = useTranslation()
  const { next } = useContext(StepperContext)
  const { loginAndFetchBookings } = useContext(StoreContext)
  const { values, setFieldError, setFieldValue } = useFormikContext()
  const [isLoading, setIsLoading] = useState(false)
  const [activationBadState, setActivationBadState] = useState(false)

  const existingName = values.existingUserName || ''
  const existingEmail = values.existingUserEmail || ''

  const handleUseExisting = () => {
    if (existingEmail) {
      setFieldValue('maskedEmail', existingEmail, false)
    }
    // Server sends email OTP to the existing email — navigate to validate it
    next('validateEmail')
  }

  const handleCreateNew = () => {
    // Drop the matched profile's name before creating a separate one. By this point
    // firstName/lastName can hold the name of the profile that already owns this phone
    // number — SocialAddPhone and SelectMethod both copy them out of the response's
    // userInfo — and InputName renders straight from these fields, so the old name would
    // be pre-filled and saved onto the new profile.
    setFieldValue('firstName', '', false)
    setFieldValue('lastName', '', false)

    // Use the social login email for a new profile instead
    const googleEmail = values.externalEmail
    if (!googleEmail) {
      next('inputName')
      return
    }

    setIsLoading(true)
    addEmail(googleEmail)
      .then(response => {
        const actions = response?.expectedActions || []
        if (response?.userId) {
          completeActivation({ termsAndConditions: true })
            .then(a => {
              if (a.userId) {
                loginAndFetchBookings(a.userId)
                emitCustomEvent('SHOW_LOGIN', false)
              }
            })
            .catch(err => setFieldError('email', err?.response?.message))
        } else if (response?.deviceActivationState === 'EMAIL_OTP_INITIATED') {
          setFieldValue('maskedEmail', response.maskedEmail, false)
          next('validateEmail')
        } else if (response?.deviceActivationState === 'EMAIL_ADDED') {
          if (actions.includes('ADD_NAME')) {
            next('inputName')
          } else {
            completeActivation({ termsAndConditions: true })
              .then(a => {
                if (a.userId) {
                  loginAndFetchBookings(a.userId)
                  emitCustomEvent('SHOW_LOGIN', false)
                }
              })
              .catch(err => setFieldError('email', err?.response?.message))
          }
        }
      })
      .catch(error => {
        setFieldError('email', error?.response?.message)
        if (isBadState(error)) setActivationBadState(true)
      })
      .finally(() => setIsLoading(false))
  }

  const buttons = [
    { func: handleCreateNew, title: t('button-create-new-account'), disabled: isLoading || activationBadState },
    { func: handleUseExisting, title: t('button-login'), disabled: isLoading || activationBadState },
  ]

  return (
    <>
      <FormContent isLoading={isLoading}>
        <div className="pt-6 pb-4 flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-[18px] font-bold tracking-[0.54px] m-0">
              {existingName ? t('header-login-as', { name: existingName }) : t('header-is-this-you')}
            </h2>
            <p className="text-body-lg text-on-surface m-0">
              {t('info-existing-account-found')}
            </p>
          </div>

          {(existingName || existingEmail) && (
            <div className="flex items-center gap-3 py-1">
              <div className="bg-surface-container p-1 rounded-[24px] shrink-0">
                <div className="flex items-center justify-center w-6 h-6 text-on-surface-variant">
                  <UserIcon />
                </div>
              </div>
              <div className="flex flex-col gap-0.5 min-w-0">
                {existingName && (
                  <p className="text-body-lg font-medium text-on-surface tracking-[-0.34px] m-0 truncate">
                    {existingName}
                  </p>
                )}
                {existingEmail && (
                  <p className="text-body-lg text-on-surface-variant m-0 truncate">
                    {existingEmail}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </FormContent>
      <ButtonDrawer buttons={buttons} isLoading={isLoading} />
    </>
  )
}
